import React, {
  useRef,
  useEffect,
  useCallback,
  useState,
  ReactNode,
  Ref,
} from "react";
import { Box } from "@mui/material";
import designTokens from "../styles/designTokens";

interface CustomScrollbarProps {
  children: ReactNode;
  height?: number | string;
  width?: number | string;
  thumbHeight?: number;
  borderRadius?: number;
  trackRight?: number;
  thumbColor?: string;
  trackInsetY?: number;
  scrollContentRef?: Ref<HTMLDivElement>;
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (!ref) return;
  if (typeof ref === "function") {
    ref(value);
    return;
  }
  (ref as { current: T | null }).current = value;
}

const CustomScrollbar = React.memo<CustomScrollbarProps>(
  function CustomScrollbar({
    children,
    height = 300,
    width = 400,
    thumbHeight = 60,
    borderRadius = 2,
    trackRight = 0,
    thumbColor = designTokens?.fill?.background?.grey80?.dark ?? "#888",
    trackInsetY = 4,
    scrollContentRef,
  }) {
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const thumbRef = useRef<HTMLDivElement>(null);
    const trackRef = useRef<HTMLDivElement>(null);
    const rafIdRef = useRef<number | null>(null);
    const roRef = useRef<ResizeObserver | null>(null);
    const parentRoRef = useRef<ResizeObserver | null>(null);
    const dragStateRef = useRef<{
      startY: number;
      startScrollTop: number;
    } | null>(null);
    const restoreUserSelectRef = useRef("");
    const [isDraggingThumb, setIsDraggingThumb] = useState(false);
    const [calculatedHeight, setCalculatedHeight] = useState<number | string>(
      () => {
        if (typeof height === "string" && height.includes("%")) {
          // 초기 렌더링 시 부모 높이를 즉시 계산 시도
          if (typeof window !== "undefined") {
            // 기본값을 더 큰 값으로 설정하여 펼쳐지는 효과 최소화
            return window.innerHeight;
          }
          return 300;
        }
        return height;
      },
    );

    const syncThumbPosition = useCallback(() => {
      const content = contentRef.current;
      const thumb = thumbRef.current;
      const track = trackRef.current;
      if (!content || !thumb || !track) return;

      // 스크롤 가능 여부 계산
      const scrollRange = Math.max(
        0,
        content.scrollHeight - content.clientHeight,
      );

      // 스크롤 불필요: thumb을 맨 위로, 트랙 숨김 처리(선택)
      if (scrollRange === 0) {
        thumb.style.top = "0px";
        track.style.opacity = "0"; // 필요 시 숨김
        track.style.pointerEvents = "none";
        return;
      } else {
        track.style.opacity = "1";
        track.style.pointerEvents = "auto";
      }

      const ratio = content.scrollTop / scrollRange;
      const trackH = track.clientHeight; // offsetHeight와 유사, 리플로우 최소화
      const maxThumbTop = Math.max(0, trackH - thumb.offsetHeight); // 음수 방지
      const top = Math.min(maxThumbTop, Math.max(0, ratio * maxThumbTop));

      thumb.style.top = `${top}px`;
    }, []);

    const scrollToThumbTop = useCallback((thumbTop: number) => {
      const content = contentRef.current;
      const thumb = thumbRef.current;
      const track = trackRef.current;
      if (!content || !thumb || !track) return;

      const scrollRange = Math.max(
        0,
        content.scrollHeight - content.clientHeight,
      );
      const maxThumbTop = Math.max(0, track.clientHeight - thumb.offsetHeight);
      if (scrollRange === 0 || maxThumbTop === 0) {
        content.scrollTop = 0;
        return;
      }

      const clampedThumbTop = Math.min(maxThumbTop, Math.max(0, thumbTop));
      content.scrollTop = (clampedThumbTop / maxThumbTop) * scrollRange;
    }, []);

    const handleThumbMouseDown = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        const content = contentRef.current;
        if (!content) return;

        event.preventDefault();
        event.stopPropagation();
        dragStateRef.current = {
          startY: event.clientY,
          startScrollTop: content.scrollTop,
        };
        restoreUserSelectRef.current = document.body.style.userSelect;
        document.body.style.userSelect = "none";
        setIsDraggingThumb(true);
      },
      [],
    );

    const handleTrackMouseDown = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target === thumbRef.current) return;

        const track = trackRef.current;
        const thumb = thumbRef.current;
        if (!track || !thumb) return;

        event.preventDefault();
        const trackRect = track.getBoundingClientRect();
        const nextThumbTop =
          event.clientY - trackRect.top - thumb.offsetHeight / 2;
        scrollToThumbTop(nextThumbTop);
      },
      [scrollToThumbTop],
    );

    // height가 "100%"인 경우 부모 컨테이너의 높이를 계산
    useEffect(() => {
      if (typeof height === "string" && height.includes("%")) {
        const updateHeight = () => {
          const container = containerRef.current?.parentElement;
          if (container) {
            const containerHeight = container.clientHeight;
            if (containerHeight > 0) {
              setCalculatedHeight(containerHeight);
            }
          }
        };

        // 초기 높이 계산 (즉시 실행, 여러 번 시도하여 정확한 높이 확보)
        updateHeight();
        // DOM이 완전히 렌더링된 후 한 번 더 확인
        const immediateCheck = setTimeout(() => {
          updateHeight();
        }, 0);

        // ResizeObserver로 부모 크기 변화 감지 (즉시 실행, requestAnimationFrame 제거)
        const container = containerRef.current?.parentElement;
        if (container && typeof ResizeObserver !== "undefined") {
          const ro = new ResizeObserver(() => {
            updateHeight();
          });
          ro.observe(container);
          parentRoRef.current = ro;
        }

        // 윈도우 리사이즈도 감지
        window.addEventListener("resize", updateHeight);

        return () => {
          clearTimeout(immediateCheck);
          if (parentRoRef.current) {
            parentRoRef.current.disconnect();
          }
          window.removeEventListener("resize", updateHeight);
        };
      } else {
        setCalculatedHeight(height);
      }
    }, [height]);

    useEffect(() => {
      if (!isDraggingThumb) return undefined;

      const handleMouseMove = (event: MouseEvent) => {
        const content = contentRef.current;
        const thumb = thumbRef.current;
        const track = trackRef.current;
        const dragState = dragStateRef.current;
        if (!content || !thumb || !track || !dragState) return;

        const scrollRange = Math.max(
          0,
          content.scrollHeight - content.clientHeight,
        );
        const maxThumbTop = Math.max(
          0,
          track.clientHeight - thumb.offsetHeight,
        );
        if (scrollRange === 0 || maxThumbTop === 0) return;

        const deltaY = event.clientY - dragState.startY;
        content.scrollTop =
          dragState.startScrollTop + (deltaY / maxThumbTop) * scrollRange;
      };

      const handleMouseUp = () => {
        dragStateRef.current = null;
        document.body.style.userSelect = restoreUserSelectRef.current;
        setIsDraggingThumb(false);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.userSelect = restoreUserSelectRef.current;
      };
    }, [isDraggingThumb]);

    useEffect(() => {
      const content = contentRef.current;
      if (!content) return;

      // 최초 동기화 (레이아웃 안정화 이후)
      rafIdRef.current = requestAnimationFrame(syncThumbPosition);

      // 스크롤/리사이즈 리스너
      const onScroll = () => {
        // 성능을 위해 rAF로 coalesce
        if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = requestAnimationFrame(syncThumbPosition);
      };

      content.addEventListener("scroll", onScroll);
      window.addEventListener("resize", onScroll);

      // 내용/레이아웃 변화 감지
      if (typeof ResizeObserver !== "undefined") {
        const ro = new ResizeObserver(onScroll);
        ro.observe(content);
        roRef.current = ro;
      }

      return () => {
        content.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onScroll);
        if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
        if (roRef.current) roRef.current.disconnect();
      };
    }, [syncThumbPosition, calculatedHeight]);

    return (
      <Box
        ref={containerRef}
        sx={{
          position: "relative",
          width,
          height: calculatedHeight,
          overflow: "hidden",
          borderRadius,
          boxSizing: "border-box",
          transition: "none",
          animation: "none",
        }}
      >
        {/* Scrollable content */}
        <Box
          ref={(element: HTMLDivElement | null) => {
            contentRef.current = element;
            assignRef(scrollContentRef, element);
          }}
          data-testid="custom-scrollbar-content"
          sx={{
            height: "100%",
            pr: 2,
            overflowY: "scroll",
            overflowX: "hidden",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            transition: "none",
            animation: "none",
          }}
        >
          <Box sx={{ pr: 1 }}>{children}</Box>
        </Box>

        {/* Custom Scrollbar Track */}
        <Box
          ref={trackRef}
          data-testid="custom-scrollbar-track"
          onMouseDown={handleTrackMouseDown}
          sx={{
            position: "absolute",
            top: trackInsetY,
            bottom: trackInsetY,
            right: trackRight,
            width: "6px",
            height: "auto",
            backgroundColor: "transparent",
            transition: "opacity 0.15s ease",
            cursor: "pointer",
          }}
        >
          {/* Scrollbar Thumb */}
          <Box
            ref={thumbRef}
            data-testid="custom-scrollbar-thumb"
            onMouseDown={handleThumbMouseDown}
            sx={{
              position: "absolute",
              width: "100%",
              height: `${thumbHeight}px`,
              backgroundColor: thumbColor,
              borderRadius: "4px",
              transition: "top 0.1s linear",
              cursor: isDraggingThumb ? "grabbing" : "grab",
            }}
          />
        </Box>
      </Box>
    );
  },
);

CustomScrollbar.displayName = "CustomScrollbar";

export default CustomScrollbar;
